import React, { useState, useEffect, useCallback, useMemo, useContext } from "react";
import { Button, Input, Modal, ModalHeader, ModalBody } from "reactstrap";
import * as XLSX from "xlsx";
import Header from "../../layout/Header/Header";
import { posGet, avisaError, dinero, numero, fechaHora } from "./posApi";
import { variante } from "./posApi";
import { useTienda, TiendaSelector } from "./TiendaSelector";
import { AuthContext } from "../../contexts/AuthContext";
import SinPermiso from "./SinPermiso";
import FotoModelo from "../Articulos/FotoModelo";

// Agrupa variantes por color conservando el orden de tallas que manda el servidor
const porColor = (vs) => {
  const g = new Map();
  vs.forEach((v) => { const k = v.color || ""; if (!g.has(k)) g.set(k, []); g.get(k).push(v); });
  return [...g.entries()];
};

function PosInventario() {
  const { user } = useContext(AuthContext);
  const ctx = useTienda(true);
  const { tienda, nombreTienda } = ctx;
  const verCostos = !!(user && user.posCostos);
  const [filas, setFilas] = useState([]);
  const [texto, setTexto] = useState("");
  const [cargando, setCargando] = useState(false);
  const [kardex, setKardex] = useState(null);
  const [vista, setVista] = useState("modelo"); // modelo | detalle
  const [agrupar, setAgrupar] = useState("total"); // todas las tiendas: total | tienda
  const [soloNegativo, setSoloNegativo] = useState(false); // lo vendido sin existencia que falta dar de entrada
  const [donde, setDonde] = useState(null); // desglose por tienda de un modelo
  const [transito, setTransito] = useState(null); // piezas en camino
  const todas = tienda === "todas";
  const porTienda = todas && agrupar === "tienda";

  const cargar = useCallback(() => {
    if (!tienda) return;
    setCargando(true);
    posGet("/inventario", { area: tienda, ...(tienda === "todas" ? { agrupar } : {}) }).then(setFilas).catch(avisaError).finally(() => setCargando(false));
    // Lo que va en camino no está en ninguna tienda; se muestra aparte (solo quien ve traspasos)
    if (user && (user.posTraspasos || user.posRecibir)) {
      posGet("/traspasos", { area: tienda, estatus: "En tránsito", ...(tienda === "todas" ? {} : { tipo: "recibidos" }) })
        .then((l) => setTransito({ piezas: l.reduce((t, x) => t + (x.piezas || 0), 0), traspasos: l.length }))
        .catch(() => setTransito(null));
    }
  }, [tienda, agrupar, user]);
  useEffect(() => { cargar(); }, [cargar]);

  const visibles = useMemo(() => {
    const t = texto.trim().toLowerCase();
    const conFiltro = soloNegativo ? filas.filter((f) => f.cantidad < 0) : filas;
    const base = t ? conFiltro.filter((f) => `${f.codigo} ${f.nombre} ${f.linea || ""} ${f.tienda || ""} ${f.color || ""} ${f.talla || ""}`.toLowerCase().includes(t)) : conFiltro;
    if (vista === "detalle") return base;
    // Por modelo: una fila por modelo (y tienda) con el desglose de sus variantes
    const g = {};
    base.forEach((f) => {
      const k = `${f.articulos}|${f.tienda || ""}`;
      if (!g[k]) g[k] = { ...f, cantidad: 0, valor: 0, variantes: [] };
      g[k].cantidad += f.cantidad;
      g[k].valor += f.valor || 0;
      if (f.color || f.talla) g[k].variantes.push(f);
    });
    return Object.values(g);
  }, [filas, texto, vista, soloNegativo]);
  const negativos = filas.filter((f) => f.cantidad < 0);
  const piezas = visibles.reduce((s, f) => s + f.cantidad, 0);
  const valor = visibles.reduce((s, f) => s + (f.valor || 0), 0);

  const verKardex = async (f) => {
    if (todas) {
      // Total de la empresa: en qué tiendas está ese modelo
      try { setDonde({ fila: f, filas: await posGet("/inventario", { area: "todas", agrupar: "tienda", articulo: f.articulos }) }); } catch (e) { avisaError(e); }
      return;
    }
    const params = { area: tienda };
    if (vista === "detalle") { params.colores = f.colores || ""; params.tallas = f.tallas || ""; }
    try { setKardex({ fila: f, movs: await posGet(`/kardex/${f.articulos}`, params) }); } catch (e) { avisaError(e); }
  };

  const excel = () => {
    const datos = visibles.map((f) => {
      const o = { Codigo: f.codigo, Modelo: f.nombre, Linea: f.linea || "" };
      if (vista === "detalle") { o.Color = f.color || ""; o.Talla = f.talla || ""; }
      o.Existencia = f.cantidad; o.Precio = f.precio;
      if (porTienda) o.Tienda = f.tienda;
      if (verCostos) o.ValorCosto = f.valor;
      return o;
    });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(datos), "Inventario");
    XLSX.writeFile(wb, `inventario_${(nombreTienda || "tiendas").replace(/\s+/g, "_")}.xlsx`);
  };

  if (!(user && user.rolTiendas && user.posInventario)) return <SinPermiso que="ver el inventario" />;

  return (
    <>
      <Header />
      <div className="card container col-12" style={{ padding: "18px 22px" }}>
        <div className="page-header">
          <div>
            <h3 className="page-title"><i className="fas fa-warehouse mr-2" style={{ color: "#06b6d4" }} />Inventario de tienda</h3>
            <p className="page-subtitle">Existencias por talla y color · clic en un renglón para ver {todas ? "en qué tiendas está" : "sus movimientos"}</p>
          </div>
          <div className="page-actions">
            <Button color="danger" size="sm" href="/PosStockBajo"><i className="fas fa-bell mr-1" />Stock bajo</Button>
            <Button color="secondary" size="sm" href="/PuntoVenta"><i className="fas fa-th-large mr-1" />Menú</Button>
          </div>
        </div>

        <div className="filter-bar">
          <TiendaSelector ctx={ctx} permiteTodas />
          <div style={{ flex: 1, minWidth: 220 }}><label>Buscar</label><Input placeholder="Código, modelo o línea" value={texto} onChange={(e) => setTexto(e.target.value)} /></div>
          {todas && (
            <div><label>Tiendas</label>
              <Input type="select" value={agrupar} onChange={(e) => setAgrupar(e.target.value)}>
                <option value="total">Total de la empresa</option><option value="tienda">Separado por tienda</option>
              </Input>
            </div>
          )}
          <div><label>Ver</label>
            <Input type="select" value={vista} onChange={(e) => setVista(e.target.value)}>
              <option value="modelo">Por modelo</option><option value="detalle">Por talla y color</option>
            </Input>
          </div>
          <div style={{ flexDirection: "row", gap: 8 }}>
            <Button color="primary" onClick={cargar} disabled={cargando}>{cargando ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-sync" />}</Button>{" "}
            <Button color="warning" onClick={excel} disabled={!visibles.length}><i className="fas fa-file-excel mr-1" />Excel</Button>
          </div>
        </div>

        <div className="stats-row">
          <div className="stat-chip">{vista === "detalle" ? "Variantes" : "Modelos"} <span>{numero(visibles.length)}</span></div>
          <div className="stat-chip">Piezas <span>{numero(piezas)}</span></div>
          {verCostos && <div className="stat-chip">Valor a costo <span>{dinero(valor)}</span></div>}
          {negativos.length > 0 && (
            <button type="button" className="stat-chip" onClick={() => setSoloNegativo(!soloNegativo)}
              title="Se vendió sin existencia en sistema; falta dar la entrada" style={{ cursor: "pointer", border: soloNegativo ? "2px solid #dc2626" : undefined }}>
              <i className="fas fa-minus-circle" style={{ color: "#dc2626" }} />En negativo <span>{numero(negativos.length)} · {numero(negativos.reduce((s, f) => s + f.cantidad, 0))} pzs</span>
              {soloNegativo && <i className="fas fa-times ml-2" style={{ color: "#64748b" }} />}
            </button>
          )}
          {transito && transito.piezas > 0 && (
            <div className="stat-chip" title="Enviadas y aún no recibidas; no cuentan en ninguna tienda">
              <i className="fas fa-truck" style={{ color: "#8b5cf6" }} />{todas ? "En camino entre tiendas" : "Por llegar"} <span>{numero(transito.piezas)} pzs</span>
            </div>
          )}
        </div>

        <div className="table-responsive">
          <table className="table table-sm table-dark-header">
            <thead>
              <tr>
                {porTienda && <th>Tienda</th>}
                <th style={{ width: 48 }} /><th>Código</th><th>Modelo</th>{vista === "detalle" ? <><th>Color</th><th>Talla</th></> : <th>Tallas y colores</th>}<th>Línea</th><th className="text-right">Precio</th><th className="text-right">Existencia</th>
                {verCostos && <th className="text-right">Valor a costo</th>}
              </tr>
            </thead>
            <tbody>
              {!visibles.length && <tr><td colSpan={10} className="text-center text-muted py-4">{cargando ? "Cargando…" : "Sin existencias"}</td></tr>}
              {visibles.slice(0, 3000).map((f) => (
                <tr key={`${f.articulos}-${f.colores || ""}-${f.tallas || ""}-${f.tienda || ""}`} onClick={() => verKardex(f)} style={{ cursor: porTienda ? "default" : "pointer" }}>
                  {porTienda && <td>{f.tienda}</td>}
                  <td><FotoModelo url={f.urlFoto} nombre={`${f.codigo} · ${f.nombre}`} size={36} /></td>
                  <td><strong>{f.codigo}</strong></td><td>{f.nombre}</td>
                  {vista === "detalle" ? (
                    <><td>{f.color || "—"}</td><td>{f.talla || "—"}</td></>
                  ) : (
                    <td style={{ fontSize: "0.78rem", whiteSpace: "nowrap" }}>
                      {porColor(f.variantes).map(([color, vs]) => (
                        <div key={color}>
                          {color && <span style={{ color: "#64748b" }}>{color}: </span>}
                          {vs.map((v, i) => (
                            <span key={i} style={{ color: v.cantidad > 0 ? "#0f172a" : "#cbd5e1" }}>
                              {i > 0 && " · "}{v.talla ? `${v.talla} ` : ""}<strong>{v.cantidad}</strong>
                            </span>
                          ))}
                        </div>
                      ))}
                    </td>
                  )}
                  <td>{f.linea}</td>
                  <td className="text-right">{dinero(f.precio)}</td>
                  <td className="text-right" style={{ fontWeight: 700, color: f.cantidad < 0 ? "#dc2626" : undefined }}>{numero(f.cantidad)}</td>
                  {verCostos && <td className="text-right">{dinero(f.valor)}</td>}
                </tr>
              ))}
            </tbody>
          </table>
          {visibles.length > 3000 && <div className="text-muted">Mostrando 3,000 de {numero(visibles.length)}; usa el buscador o descarga el Excel.</div>}
        </div>
      </div>

      <Modal isOpen={!!donde} toggle={() => setDonde(null)} size="lg">
        <ModalHeader toggle={() => setDonde(null)}>{donde && `${donde.fila.codigo} · ${donde.fila.nombre}${vista === "detalle" && variante(donde.fila) ? " · " + variante(donde.fila) : ""} · por tienda`}</ModalHeader>
        <ModalBody>
          {donde && (() => {
            // En la vista por talla y color solo esa variante; si no, todo el modelo
            const sel = donde.filas.filter((x) => vista !== "detalle" || (String(x.colores || "") === String(donde.fila.colores || "") && String(x.tallas || "") === String(donde.fila.tallas || "")));
            const g = new Map();
            sel.forEach((x) => { if (!g.has(x.tienda)) g.set(x.tienda, []); g.get(x.tienda).push(x); });
            const filasT = [...g.entries()].map(([t, vs]) => ({ t, vs, n: vs.reduce((s, v) => s + v.cantidad, 0) })).sort((a, b) => b.n - a.n);
            return (
              <table className="table table-sm">
                <thead><tr><th>Tienda</th>{vista !== "detalle" && <th>Tallas y colores</th>}<th className="text-right">Existencia</th></tr></thead>
                <tbody>
                  {!filasT.length && <tr><td colSpan={3} className="text-center text-muted">Sin existencia en ninguna tienda</td></tr>}
                  {filasT.map(({ t, vs, n }) => (
                    <tr key={t}>
                      <td>{t}</td>
                      {vista !== "detalle" && (
                        <td style={{ fontSize: "0.78rem" }}>
                          {porColor(vs.filter((v) => v.color || v.talla)).map(([color, l]) => (
                            <div key={color}>{color && <span style={{ color: "#64748b" }}>{color}: </span>}{l.map((v, i) => <span key={i}>{i > 0 && " · "}{v.talla ? `${v.talla} ` : ""}<strong>{v.cantidad}</strong></span>)}</div>
                          ))}
                        </td>
                      )}
                      <td className="text-right" style={{ fontWeight: 700 }}>{numero(n)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            );
          })()}
        </ModalBody>
      </Modal>

      <Modal isOpen={!!kardex} toggle={() => setKardex(null)} size="lg">
        <ModalHeader toggle={() => setKardex(null)}>{kardex && `${kardex.fila.codigo} · ${kardex.fila.nombre}${vista === "detalle" && variante(kardex.fila) ? " · " + variante(kardex.fila) : ""}`}</ModalHeader>
        <ModalBody>
          {kardex && (
            <table className="table table-sm">
              <thead><tr><th>Fecha</th><th>Movimiento</th><th>Folio</th><th>Color · Talla</th><th className="text-right">Cantidad</th><th className="text-right">Existencia</th><th>Usuario</th></tr></thead>
              <tbody>
                {!kardex.movs.length && <tr><td colSpan={7} className="text-center text-muted">Sin movimientos del Punto de Venta</td></tr>}
                {kardex.movs.map((m) => (
                  <tr key={m._id}>
                    <td>{fechaHora(m.fecha)}</td><td>{m.tipo}</td><td>{m.folio}</td><td>{variante(m) || "—"}</td>
                    <td className="text-right" style={{ color: m.cantidad < 0 ? "#dc2626" : "#059669", fontWeight: 700 }}>{m.cantidad > 0 ? "+" : ""}{m.cantidad}</td>
                    <td className="text-right">{m.existencia}</td><td>{m.user ? m.user.nombre : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </ModalBody>
      </Modal>
    </>
  );
}

export default PosInventario;
